import { useState } from 'react';
import { Building2, Plus, ArrowRight, Shield, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Alert } from '../components/Alert';
import { Logo } from '../components/Logo';

interface OrgPickerProps {
  onSelect: () => void;
  onCreateNew: () => void;
}

export function OrgPickerPage({ onSelect, onCreateNew }: OrgPickerProps) {
  const { session, activeOrg, switchOrg } = useAuth();
  const [loadingOrgId, setLoadingOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const memberships = session?.memberships || [];

  const handleSelect = async (orgId: string) => {
    setLoadingOrgId(orgId);
    setError(null);
    try {
      await switchOrg(orgId);
      onSelect();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch organisation');
      setLoadingOrgId(null);
    }
  };

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: 'var(--space-8) 0', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Logo variant="lockup" size={36} tagline="ORGANISATION SELECTION" />
        <Button variant="secondary" size="sm" onClick={onCreateNew} icon={<Plus size={14} />}>
          New Organisation
        </Button>
      </div>

      <div>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)' }}>Choose an Organisation</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-base)', marginTop: 6 }}>
          Select the organisation workspace you wish to manage in CYRN Nexus.
        </p>
      </div>

      {error && (
        <Alert tone="danger" title="Selection error">
          {error}
        </Alert>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {memberships.length === 0 ? (
          <Card padding="var(--space-8)" style={{ textAlign: 'center' }}>
            <Building2 size={40} color="var(--text-tertiary)" style={{ margin: '0 auto 12px' }} />
            <h3 style={{ fontSize: 'var(--text-lg)' }}>No organisations found</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4, marginBottom: 20 }}>
              You are not a member of any organisation yet. Create your first workspace to start.
            </p>
            <Button variant="primary" onClick={onCreateNew} icon={<Plus size={16} />}>
              Create First Organisation
            </Button>
          </Card>
        ) : (
          memberships.map((m) => {
            const org = m.organisation;
            if (!org) return null;
            const isCurrent = org.id === activeOrg?.id;
            const isLoadingThis = loadingOrgId === org.id;

            return (
              <Card
                key={org.id}
                padding="var(--space-4) var(--space-5)"
                style={{
                  cursor: 'pointer',
                  border: isCurrent ? '1px solid var(--accent)' : '1px solid var(--border-default)',
                  transition: 'transform var(--duration-fast), border-color var(--duration-fast)',
                }}
                onClick={() => handleSelect(org.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 'var(--radius-md)',
                        background: isCurrent ? 'var(--accent-soft)' : 'var(--navy-700)',
                        border: '1px solid var(--border-subtle)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isCurrent ? 'var(--accent)' : 'var(--text-secondary)',
                      }}
                    >
                      <Building2 size={22} />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--text-primary)' }}>
                          {org.display_name}
                        </span>
                        {isCurrent && (
                          <Badge tone="accent" dot>
                            Active Workspace
                          </Badge>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                        <span>Role: <strong style={{ color: 'var(--text-secondary)' }}>{m.role}</strong></span>
                        <span>•</span>
                        <span>Slug: <code>{org.slug}</code></span>
                        <span>•</span>
                        <span>Country: {org.country}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isLoadingThis ? (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--accent)' }}>Switching...</span>
                    ) : (
                      <Button
                        size="sm"
                        variant={isCurrent ? 'ghost' : 'secondary'}
                        icon={isCurrent ? <CheckCircle2 size={14} color="var(--accent)" /> : <ArrowRight size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(org.id);
                        }}
                      >
                        {isCurrent ? 'Open' : 'Select'}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 'var(--space-4)' }}>
        <Shield size={14} color="var(--accent)" />
        <span>CYRN Nexus Tenant Authority • Session tokens bound to selected organisation</span>
      </div>
    </div>
  );
}
